import type { BrandOverrides, BrandTokens } from '@alison/whitelabel';
import { alpha } from '@mui/material/styles';
export const fonts = {
  sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
} as const;

export const SPACING_UNIT = 5;

export const HTML_FONT_SIZE = 12;

export const brand = {
  blue: '#155DFC',
  blueHover: '#1447E6',
  bgBlue: '#EFF6FF',
  purple: '#9810FA',
  bgPurple: '#5767c3',
  violet: '#831DFB',
} as const;

export const secondary = {
  main: '#E60076',
  dark: '#B0004E',
  light: '#FAF5FF',
} as const;

export const gradients = {
  primary: 'linear-gradient(90deg, #155DFC 0%, #9810FA 100%)',
  primaryHover: 'linear-gradient(90deg, #1447E6 0%, #7B0DC8 100%)',
  progress: 'linear-gradient(90deg, #51A2FF 0%, #C27AFF 100%)',
  subtle: 'linear-gradient(90deg, #EFF6FF 0%, #FAF5FF 100%)',
  unlock: 'linear-gradient(180deg, #FF4185 0%, #F72872 100%)',
} as const;

export const BAR_FILL_FADE_FROM = 0.35;

export function barFill(color: string): string {
  return `linear-gradient(90deg, ${alpha(color, BAR_FILL_FADE_FROM)}, ${color})`;
}

export const gradientText = {
  background: gradients.primary,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
} as const;

export const text = {
  primary: '#1D355C',
  secondary: '#6A7282',
  disabled: '#99A1AF',
  muted: '#62748E',
} as const;

export const white = '#FFFFFF';

export const background = {
  page: '#F8FAFC',
  paper: '#FFFFFF',
  platform: '#F1F5F9',
  muted: '#E2E8F0',
  icon: '#31343A',
} as const;

export const border = {
  default: '#E2E8F0',
  strong: '#CAD5E2',
  light: '#C6D2FF',
  primary: '#2B7FFF',
  dashed: '#818897',
} as const;

export const state = {
  success: { main: '#00A63E', bg: '#D1FAE5', dark: '#2C9C6D' },
  warning: { main: '#F59E0B', bg: '#FEF3C7', dark: '#FFB640' },
  error: { main: '#EF4444', bg: '#FEE2E2', dark: '#CD5564' },
  info: { main: '#155DFC', bg: '#EFF6FF' },
} as const;

export const category = {
  attract: { main: '#4285F4', bg: '#EFF6FF' },
  brand: { main: '#FBBC04', bg: '#FFF9E7' },
  connect: { main: '#EA4335', bg: '#FDF2F8' },
  direct: { main: '#34A853', bg: '#F0FDF4' },
} as const;

// TODO(Mali): copied from Alison 1's ElementCategoryTag because alison2 has no Elements
// Explorer yet. Once it lands here, source these from it and delete this comment.
export const attribute = {
  product: { main: '#8200DB', bg: '#FAF5FF', border: '#E9D4FF' },
  graphics: { main: '#F59E0B', bg: '#FFF7ED', border: '#FFD6A7' },
  story: { main: '#10B981', bg: '#E7F9E9', border: '#B9F8CF' },
  structure: { main: '#1447E6', bg: '#E9F0FF', border: '#BEDBFF' },
  conceptual: { main: '#EC4899', bg: 'rgba(244, 91, 155, 0.1)', border: 'rgba(255, 59, 138, 0.2)' },
} as const;

export const score = {
  low: { main: '#CD5564', bg: '#FEECEC', text: '#CD5564' },
  medium: { main: '#FFB640', bg: '#FEF3C7', text: '#A88924' },
  high: { main: '#2C9C6D', bg: '#E7F9E9', text: '#2C9C6D' },
} as const;

export const medal = {
  gold: '#9C7B2E',
  silver: '#94A3B8',
  bronze: '#B5824E',
} as const;

export const network = {
  meta: '#1877F2',
  google: '#FBBC04',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  slack: '#4A154B',
} as const;

export const chartSeries = [
  network.meta,
  category.connect.main,
  attribute.story.main,
  state.warning.main,
  attribute.conceptual.main,
] as const;

export const elementCategory = {
  Product: '#8200DB',
  Structure: '#1447E6',
  Story: '#10B981',
  Graphics: '#F59E0B',
  Conceptual: '#EC4899',
  Contextual: '#EC4899',
  Visual: '#F59E0B',
  Emotion: '#10B981',
  Relevance: '#EC4899',
  Action: '#1447E6',
  Brand: '#0A66C2',
  Sound: '#7C3AED',
  Settings: '#0D9488',
  default: text.muted,
} as const;

export const flightStage = {
  pre: { fg: '#1D4ED8', bg: '#EEF4FF' },
  mid: { fg: '#B45309', bg: '#FEF3C7' },
  post: { fg: '#1F7A4D', bg: '#E7F9E9' },
} as const;

export const radius = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 10,
  xl: 16,
  xxl: 20,
  pill: 9999,
} as const;

export const shadows = {
  chrome: '0px 1px 2px -1px rgba(0,0,0,0.10)',
  card: '0px 1px 3px 0px rgba(0,0,0,0.02), 0px 10px 40px 0px rgba(0,0,0,0.03), 0px 2px 8px 0px rgba(0,0,0,0.05)',
  tooltip: '-8px 4px 20px 0px rgba(198,210,255,0.26)',
  buttonPrimary: '0px 4px 12px 0px rgba(21,93,252,0.14)',
  dropdown: '1px 4px 12px 0px rgba(0,0,0,0.145)',
} as const;

export const layout = {
  sidebarWidth: 75,
  minViewport: 992,
} as const;

/** The Alison brand, assembled from the token constants above. */
export const alisonBrand: BrandTokens = {
  fontFamily: fonts.sans,
  primary: { main: brand.blue, dark: brand.blueHover, light: brand.bgBlue, bg: state.info.bg },
  secondary: { main: secondary.main, dark: secondary.dark, light: secondary.light },
  info: { main: state.info.main, bg: state.info.bg },
  text,
  background,
  border,
  gradients,
};

export function mergeBrand(overrides: BrandOverrides = {}): BrandTokens {
  return {
    fontFamily: overrides.fontFamily ?? alisonBrand.fontFamily,
    primary: { ...alisonBrand.primary, ...overrides.primary },
    secondary: { ...alisonBrand.secondary, ...overrides.secondary },
    info: { ...alisonBrand.info, ...overrides.info },
    text: { ...alisonBrand.text, ...overrides.text },
    background: { ...alisonBrand.background, ...overrides.background },
    border: { ...alisonBrand.border, ...overrides.border },
    gradients: { ...alisonBrand.gradients, ...overrides.gradients },
  };
}
